import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Event as EventIcon,
  SmartToy as SmartToyIcon,
  AccountTree as WorkflowIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { ticketsApi, benutzerApi, adressenApi } from '../services/api';
import type { Ticket, UpdateTicketRequest, Benutzer, WorkflowAnalysisResponse, Adresse } from '../types/api';
import { milestoneService, type Milestone } from '../services/milestoneService';
import TicketDialog from '../components/TicketDialog';
import workflowService from '../services/workflowService';

const Tickets: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [users, setUsers] = useState<Benutzer[]>([]);
  const [adressen, setAdressen] = useState<Adresse[]>([]);

  // Termin-Dialog State
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [selectedTicketForAppointment, setSelectedTicketForAppointment] = useState<Ticket | null>(null);
  const [appointmentData, setAppointmentData] = useState({
    empfaengerEmail: '',
    ort: '',
    nachricht: '',
  });
  const [sendingAppointment, setSendingAppointment] = useState(false);

  // Workflow-Dialog State
  const [workflowAnalysisDialogOpen, setWorkflowAnalysisDialogOpen] = useState(false);
  const [selectedTicketForWorkflow, setSelectedTicketForWorkflow] = useState<Ticket | null>(null);
  const [workflowAnalysis, setWorkflowAnalysis] = useState<WorkflowAnalysisResponse | null>(null);
  const [analyzingWorkflow, setAnalyzingWorkflow] = useState(false);
  const [creatingWorkflow, setCreatingWorkflow] = useState(false);
  const [triggeringNextStep, setTriggeringNextStep] = useState(false);

  useEffect(() => {
    loadTickets();
    loadMilestones();
    loadUsers();
    loadAdressen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadMilestones = async () => {
    try {
      const data = await milestoneService.getAll();
      setMilestones(data);
    } catch (error) {
      console.error('Fehler beim Laden der Meilensteine:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await benutzerApi.getMandantBenutzer();
      setUsers(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    }
  };

  const loadAdressen = async () => {
    try {
      const response = await adressenApi.getAll({ take: 1000 });
      setAdressen(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Adressen:', error);
    }
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = statusFilter ? { status: statusFilter, take: 500 } : { take: 500 };
      const response = await ticketsApi.getAll(params);
      setTickets(response.data);
    } catch (err: any) {
      console.error('Tickets-Fehler:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (ticket?: Ticket) => {
    setEditingTicket(ticket || null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTicket(null);
  };

  const handleSave = async (data: any) => {
    try {
      const { milestoneID, ...dataWithoutMilestone } = data;

      // Wann-Feld: Sende lokale Zeit ohne UTC-Konvertierung
      let wannValue: string | undefined = undefined;
      if (data.wann) {
        // Formatiere als ISO-String ohne Zeitzonen-Konvertierung
        // data.wann ist bereits im Format "YYYY-MM-DDTHH:mm"
        // Füge Sekunden und Millisekunden hinzu für vollständiges ISO-Format
        wannValue = `${data.wann}:00.000`;
      }

      const dataToSend = {
        ...dataWithoutMilestone,
        wann: wannValue,
      };

      if (editingTicket) {
        const response = await ticketsApi.update(editingTicket.ticketID, dataToSend as UpdateTicketRequest);
        setTickets(tickets.map((t) => (t.ticketID === editingTicket.ticketID ? response.data : t)));
      } else {
        const response = await ticketsApi.create(dataToSend);
        setTickets([response.data, ...tickets]);
      }
      handleCloseDialog();
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      alert('Fehler beim Speichern des Tickets: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (ticketId: number) => {
    if (!window.confirm('Ticket wirklich löschen?')) return;

    try {
      await ticketsApi.delete(ticketId);
      setTickets(tickets.filter((t) => t.ticketID !== ticketId));
    } catch (err: any) {
      alert('Fehler beim Löschen des Tickets');
    }
  };

  const handleOpenAppointmentDialog = (ticket: Ticket) => {
    if (!ticket.wann) {
      alert('Dieses Ticket hat keinen Termin (Wann-Feld ist leer)');
      return;
    }
    setSelectedTicketForAppointment(ticket);
    setAppointmentData({
      empfaengerEmail: '',
      ort: '',
      nachricht: '',
    });
    setAppointmentDialogOpen(true);
  };

  const handleCloseAppointmentDialog = () => {
    setAppointmentDialogOpen(false);
    setSelectedTicketForAppointment(null);
  };

  const handleSendAppointment = async () => {
    if (!selectedTicketForAppointment) return;

    if (!appointmentData.empfaengerEmail.trim()) {
      alert('Bitte E-Mail-Adresse eingeben');
      return;
    }

    try {
      setSendingAppointment(true);
      await ticketsApi.sendAppointment(selectedTicketForAppointment.ticketID, {
        empfaengerEmail: appointmentData.empfaengerEmail,
        ort: appointmentData.ort || undefined,
        nachricht: appointmentData.nachricht || undefined,
      });
      alert('Termin-Einladung erfolgreich versendet!');
      handleCloseAppointmentDialog();
    } catch (err: any) {
      console.error('Fehler beim Versenden des Termins:', err);
      alert('Fehler beim Versenden: ' + (err.response?.data?.message || err.message));
    } finally {
      setSendingAppointment(false);
    }
  };

  // Workflow-Handler
  const handleOpenWorkflowAnalysis = async (ticket: Ticket) => {
    setSelectedTicketForWorkflow(ticket);
    setWorkflowAnalysis(null);
    setWorkflowAnalysisDialogOpen(true);

    try {
      setAnalyzingWorkflow(true);
      const analysis = await workflowService.analyzeForWorkflow(ticket.ticketID);
      setWorkflowAnalysis(analysis);
    } catch (err: any) {
      console.error('Fehler bei Workflow-Analyse:', err);
      alert('Fehler bei der KI-Analyse: ' + (err.response?.data?.message || err.message));
    } finally {
      setAnalyzingWorkflow(false);
    }
  };

  const handleCloseWorkflowAnalysis = () => {
    setWorkflowAnalysisDialogOpen(false);
    setSelectedTicketForWorkflow(null);
    setWorkflowAnalysis(null);
  };

  const handleCreateWorkflow = async () => {
    if (!selectedTicketForWorkflow || !workflowAnalysis) return;

    try {
      setCreatingWorkflow(true);
      const workflowName = `${workflowAnalysis.kategorie} - ${selectedTicketForWorkflow.titel}`;
      const result = await workflowService.createWorkflow({
        ticketID: selectedTicketForWorkflow.ticketID,
        kategorie: workflowAnalysis.kategorie,
        workflowName: workflowName,
        schritte: workflowAnalysis.schritte,
      });

      alert(`Workflow "${result.name}" erfolgreich erstellt mit ${result.anzahlSchritte} Schritten!`);
      handleCloseWorkflowAnalysis();
      loadTickets(); // Tickets neu laden um Workflow-Infos zu sehen
    } catch (err: any) {
      console.error('Fehler beim Erstellen des Workflows:', err);
      alert('Fehler beim Erstellen: ' + (err.response?.data?.message || err.message));
    } finally {
      setCreatingWorkflow(false);
    }
  };

  const handleTriggerNextStep = async (ticketId: number) => {
    try {
      setTriggeringNextStep(true);
      const result = await workflowService.triggerNextStep(ticketId);

      if (result.nextTicketID) {
        alert(`Nächster Schritt "${result.nextTicketTitel}" wurde automatisch erstellt!`);
        loadTickets();
      } else if (result.workflowAbgeschlossen) {
        alert('Workflow wurde abgeschlossen!');
        loadTickets();
      } else {
        alert(result.nachricht);
      }
    } catch (err: any) {
      console.error('Fehler beim Auslösen des nächsten Schritts:', err);
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    } finally {
      setTriggeringNextStep(false);
    }
  };

  const handleStatusChange = async (ticketId: number, newStatus: string) => {
    try {
      const response = await ticketsApi.updateStatus(ticketId, { status: newStatus });
      setTickets(tickets.map((t) => (t.ticketID === ticketId ? response.data : t)));

      // Automatisch nächsten Schritt auslösen wenn Ticket Teil eines Workflows ist und erledigt wird
      const updatedTicket = response.data;
      if (newStatus === 'Erledigt' && updatedTicket.workflowID && !updatedTicket.nextTicketID) {
        const shouldTrigger = window.confirm(
          'Dieses Ticket ist Teil eines Workflows. Möchten Sie den nächsten Schritt automatisch erstellen?'
        );
        if (shouldTrigger) {
          await handleTriggerNextStep(ticketId);
        }
      }
    } catch (err: any) {
      alert('Fehler beim Ändern des Status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Offen':
        return 'error';
      case 'In Bearbeitung':
        return 'warning';
      case 'Erledigt':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (prioritaet?: string) => {
    switch (prioritaet) {
      case 'Niedrig':
        return 'info';
      case 'Mittel':
        return 'default';
      case 'Hoch':
        return 'warning';
      case 'Kritisch':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch {
      return '-';
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Tickets
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="">Alle</MenuItem>
                <MenuItem value="Offen">Offen</MenuItem>
                <MenuItem value="In Bearbeitung">In Bearbeitung</MenuItem>
                <MenuItem value="Erledigt">Erledigt</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Aktualisieren">
              <IconButton onClick={loadTickets} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Neues Ticket
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Titel</TableCell>
                  <TableCell>Was</TableCell>
                  <TableCell>Wann</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priorität</TableCell>
                  <TableCell>Workflow</TableCell>
                  <TableCell>Besitzer</TableCell>
                  <TableCell>Ausführender</TableCell>
                  <TableCell>Meilenstein</TableCell>
                  <TableCell>Erstellt</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      Keine Tickets vorhanden
                    </TableCell>
                  </TableRow>
                ) : (
                  tickets.map((ticket) => (
                    <TableRow key={ticket.ticketID} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {ticket.automatischErstellt && (
                            <Tooltip title="Automatisch erstellt">
                              <AssignmentIcon fontSize="small" color="primary" />
                            </Tooltip>
                          )}
                          {ticket.executedAt && (
                            <Tooltip title={`Automatisch ausgeführt am ${formatDate(ticket.executedAt)}`}>
                              <SmartToyIcon fontSize="small" color="success" />
                            </Tooltip>
                          )}
                          {ticket.titel}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {ticket.was}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(ticket.wann)}</TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={ticket.status}
                            onChange={(e) => handleStatusChange(ticket.ticketID, e.target.value)}
                            renderValue={(value) => (
                              <Chip
                                label={value}
                                color={getStatusColor(value)}
                                size="small"
                              />
                            )}
                          >
                            <MenuItem value="Offen">Offen</MenuItem>
                            <MenuItem value="In Bearbeitung">In Bearbeitung</MenuItem>
                            <MenuItem value="Erledigt">Erledigt</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.prioritaet || 'Mittel'}
                          color={getPriorityColor(ticket.prioritaet)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {ticket.workflowID ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title={`Workflow: ${ticket.workflowKategorie || 'Unbekannt'}`}>
                              <Chip
                                icon={<WorkflowIcon />}
                                label={`Schritt ${ticket.schrittNummer}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Tooltip>
                          </Box>
                        ) : (
                          <Tooltip title="Workflow analysieren">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenWorkflowAnalysis(ticket)}
                              color="secondary"
                              disabled={analyzingWorkflow || triggeringNextStep}
                            >
                              <PsychologyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {ticket.erstellerName || 'Unbekannt'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {ticket.zugewiesenerName || 'Nicht zugewiesen'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {ticket.milestoneID ? (
                          <Chip
                            label={milestones.find(m => m.milestoneID === ticket.milestoneID)?.name || 'Unbekannt'}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(ticket.erstelltAm)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Bearbeiten">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(ticket)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {ticket.wann && (
                          <Tooltip title="Termin versenden">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenAppointmentDialog(ticket)}
                              color="success"
                            >
                              <EventIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(ticket.ticketID)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog für Erstellen/Bearbeiten mit Tabs */}
      <TicketDialog
        open={dialogOpen}
        ticket={editingTicket}
        users={users}
        adressen={adressen}
        milestones={milestones}
        onClose={handleCloseDialog}
        onSave={handleSave}
      />

      {/* Dialog für Termin versenden */}
      <Dialog open={appointmentDialogOpen} onClose={handleCloseAppointmentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EventIcon />
            Termin-Einladung versenden
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTicketForAppointment && (
            <Box mb={2}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {selectedTicketForAppointment.titel}
                </Typography>
                <Typography variant="body2">
                  Termin: {formatDate(selectedTicketForAppointment.wann)}
                </Typography>
              </Alert>
            </Box>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="E-Mail-Adresse des Empfängers"
                type="email"
                value={appointmentData.empfaengerEmail}
                onChange={(e) => setAppointmentData({ ...appointmentData, empfaengerEmail: e.target.value })}
                required
                placeholder="beispiel@email.de"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ort (optional)"
                value={appointmentData.ort}
                onChange={(e) => setAppointmentData({ ...appointmentData, ort: e.target.value })}
                placeholder="z.B. Büro, Besprechungsraum, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Zusätzliche Nachricht (optional)"
                value={appointmentData.nachricht}
                onChange={(e) => setAppointmentData({ ...appointmentData, nachricht: e.target.value })}
                placeholder="Zusätzliche Informationen für den Termin..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAppointmentDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSendAppointment}
            disabled={sendingAppointment || !appointmentData.empfaengerEmail.trim()}
            startIcon={sendingAppointment ? <CircularProgress size={16} /> : <EventIcon />}
          >
            {sendingAppointment ? 'Sendet...' : 'Termin versenden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog für Workflow-Analyse */}
      <Dialog open={workflowAnalysisDialogOpen} onClose={handleCloseWorkflowAnalysis} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PsychologyIcon color="secondary" />
            KI-Workflow-Analyse
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTicketForWorkflow && (
            <Box mb={2}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {selectedTicketForWorkflow.titel}
                </Typography>
                <Typography variant="body2">
                  {selectedTicketForWorkflow.was}
                </Typography>
              </Alert>
            </Box>
          )}

          {analyzingWorkflow ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={48} color="secondary" />
              <Typography variant="body1" sx={{ mt: 2 }}>
                KI analysiert Ticket...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Erkennung der Workflow-Kategorie und Generierung der Folgeschritte
              </Typography>
            </Box>
          ) : workflowAnalysis ? (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Erkannte Kategorie
                </Typography>
                <Chip
                  icon={<WorkflowIcon />}
                  label={workflowAnalysis.kategorie}
                  color="primary"
                  sx={{ fontSize: '1.1rem', py: 2 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Provider: {workflowAnalysis.provider}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Vorgeschlagene Workflow-Schritte ({workflowAnalysis.schritte.length})
                </Typography>
                <Stepper orientation="vertical" activeStep={-1}>
                  {workflowAnalysis.schritte.map((schritt, index) => (
                    <Step key={index} completed={false}>
                      <StepLabel>
                        <Typography variant="subtitle1">{schritt.titel}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {schritt.was}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Priorität: {schritt.prioritaet}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              {workflowAnalysis.grund && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    KI-Begründung:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {workflowAnalysis.grund}
                  </Typography>
                </Box>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Wenn Sie den Workflow erstellen, wird das aktuelle Ticket als erster Schritt markiert.
                  Die weiteren Schritte werden automatisch erstellt, sobald der vorherige Schritt abgeschlossen ist.
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Alert severity="error">
              Keine Analyse-Ergebnisse verfügbar. Bitte versuchen Sie es erneut.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWorkflowAnalysis}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreateWorkflow}
            disabled={creatingWorkflow || !workflowAnalysis || analyzingWorkflow}
            startIcon={creatingWorkflow ? <CircularProgress size={16} /> : <WorkflowIcon />}
            color="primary"
          >
            {creatingWorkflow ? 'Erstellt...' : 'Workflow erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tickets;
